/**
 * PeopleListCard.tsx
 *
 * Renders the `people_list` envelope data emitted by onething_list_users,
 * onething_get_team_members, and onething_get_direct_reports.
 */

import { Users, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getAvatarGradient, getInitials } from "./avatarColor";

// ---------------------------------------------------------------------------
// Data shape emitted by the BE envelope for kind "people_list"
// ---------------------------------------------------------------------------

export interface PersonRow {
  pid: string;
  fullName: string;
  position?: string;
  email?: string;
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

function PersonRowItem({ row }: { row: PersonRow }) {
  return (
    <div className="flex items-start gap-2.5 mx-1 rounded-lg px-2 py-2.5">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${getAvatarGradient(row.pid)}`}
      >
        {getInitials(row.fullName)}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold tracking-tight leading-tight">{row.fullName}</p>
        {row.position && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{row.position}</p>
        )}
        {row.email && (
          <p className="text-[11px] text-muted-foreground/70 mt-0.5">{row.email}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface PeopleListCardProps {
  summary: string;
  rows: PersonRow[];
}

export function PeopleListCard({ summary, rows }: PeopleListCardProps) {
  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Users className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No people found.</span>
          </div>
        ) : (
          <div>
            {rows.map((row) => (
              <PersonRowItem key={row.pid} row={row} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
